import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    TextField,
    Alert
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import axios from '../../../api/axios';

function ManageAccountDialog({ open, onClose, userId, userName }) {
    const [resetLink, setResetLink] = useState('');
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const handleSendPasswordResetEmail = async () => {
        try {
            setError('');
            setSuccessMessage('');
            const response = await axios.post('user/send-password-reset-email/', {
                user_id: userId
            });
            setSuccessMessage(response.data.message);
        } catch (error) {
            console.error('Error sending password reset email:', error);
            setError(error.response?.data?.error || 'Failed to send password reset email');
        }
    };

    const handleGeneratePasswordResetLink = async () => {
        try {
            setError('');
            const response = await axios.post('user/generate-password-reset-link/', {
                user_id: userId
            });
            setResetLink(response.data.reset_link);
        } catch (error) {
            console.error('Error generating password reset link:', error);
            setError(error.response?.data?.error || 'Failed to generate password reset link');
        }
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(resetLink);
    };

    const handleClose = () => {
        setResetLink('');
        setError('');
        setSuccessMessage('');
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Manage Account - {userName}
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Choose an option to help the user reset their password:
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {successMessage && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {successMessage}
                    </Alert>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={<EmailIcon />}
                        onClick={handleSendPasswordResetEmail}
                        size="large"
                        sx={{ justifyContent: 'flex-start', p: 2 }}
                    >
                        Send Password Reset Email
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<LinkIcon />}
                        onClick={handleGeneratePasswordResetLink}
                        size="large"
                        sx={{ justifyContent: 'flex-start', p: 2 }}
                    >
                        Generate Password Reset Link
                    </Button>
                </Box>

                {resetLink && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Generated Password Reset Link:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                                fullWidth
                                value={resetLink}
                                variant="outlined"
                                size="small"
                                InputProps={{
                                    readOnly: true,
                                }}
                            />
                            <Button
                                variant="outlined"
                                startIcon={<ContentCopyIcon />}
                                onClick={handleCopyLink}
                                size="small"
                            >
                                Copy
                            </Button>
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
            </DialogActions>
        </Dialog>
    );
}

export default ManageAccountDialog;
